// FlaggedSubmissionsView.jsx - Head marker view of submissions flagged for review
import React from 'react';
import { AlertTriangle, File } from 'lucide-react';
import { getAuthenticatedFileUrl } from '../firebase/submissions';

function FlaggedSubmissionsView({
  flaggedSubmissions,
  homeworkNames,
  onReturnToMarker,
  onReleaseToPool,
  onReviewMarking
}) {
  const openFile = async (filePath) => {
    try {
      const url = await getAuthenticatedFileUrl(filePath);
      if (url) {
        window.open(url, '_blank');
      } else {
        alert('Unable to access this file. Please contact support.');
      }
    } catch (error) {
      console.error('Error opening file:', error);
      alert('Error accessing file. Please try again.');
    }
  };

  return (
    <div style={{
      background: 'white',
      borderRadius: '12px',
      padding: '24px',
      boxShadow: '0 2px 8px rgba(0,0,0,0.05)'
    }}>
      <h2 style={{ margin: '0 0 16px 0' }}>Flagged for Review</h2>

      {flaggedSubmissions.length === 0 ? (
        <div style={{
          textAlign: 'center',
          padding: '60px',
          color: '#7f8c8d'
        }}>
          <div style={{ fontSize: '18px', fontWeight: '600', marginBottom: '8px' }}>
            Nothing flagged right now
          </div>
          <div>All caught up!</div>
        </div>
      ) : (
        <div style={{ display: 'grid', gap: '16px' }}>
          {flaggedSubmissions.map(submission => (
            <div
              key={submission.id}
              style={{
                border: '2px solid #f39c12',
                borderRadius: '8px',
                padding: '20px',
                background: '#fffaf0'
              }}
            >
              <div style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'flex-start',
                gap: '20px'
              }}>
                <div style={{ flex: 1 }}>
                  <div style={{
                    fontSize: '20px',
                    fontWeight: '600',
                    color: '#2c3e50',
                    marginBottom: '8px',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '12px'
                  }}>
                    {submission.studentName}
                    {submission.cohortName && (
                      <span style={{
                        fontSize: '14px',
                        fontWeight: '500',
                        color: '#667eea',
                        background: '#eef2ff',
                        padding: '4px 12px',
                        borderRadius: '6px'
                      }}>
                        {submission.cohortName}
                      </span>
                    )}
                  </div>

                  <div style={{
                    display: 'flex',
                    gap: '16px',
                    fontSize: '14px',
                    color: '#7f8c8d',
                    marginBottom: '12px'
                  }}>
                    <span>{homeworkNames[submission.homeworkType] || submission.homeworkType}</span>
                    {submission.flaggedAt && (
                      <span>Flagged {submission.flaggedAt.toDate().toLocaleDateString()}</span>
                    )}
                    {submission.flaggedByName && (
                      <span>| Flagged by: {submission.flaggedByName}</span>
                    )}
                  </div>

                  {submission.flagComment && (
                    <div style={{
                      display: 'flex',
                      gap: '10px',
                      padding: '12px',
                      background: 'white',
                      border: '1px solid #f5cba7',
                      borderRadius: '6px',
                      fontSize: '14px',
                      color: '#7d5a1e',
                      marginBottom: '12px'
                    }}>
                      <AlertTriangle size={18} color="#f39c12" style={{ flexShrink: 0, marginTop: '2px' }} />
                      <div>{submission.flagComment}</div>
                    </div>
                  )}

                  {(submission.originalFilePaths || submission.originalFileUrls) && (
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                      {(submission.originalFilePaths || submission.originalFileUrls).map((filePath, i) => (
                        <button
                          key={i}
                          onClick={() => openFile(filePath)}
                          style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: '6px',
                            padding: '6px 12px',
                            background: '#f7fafc',
                            border: '1px solid #e2e8f0',
                            borderRadius: '6px',
                            fontSize: '12px',
                            color: '#2d3748',
                            cursor: 'pointer'
                          }}
                        >
                          <File size={12} />
                          {(submission.fileNames && submission.fileNames[i]) || `File ${i + 1}`}
                        </button>
                      ))}
                    </div>
                  )}
                </div>

                <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', minWidth: '220px' }}>
                  {onReviewMarking && (
                    <button
                      onClick={() => onReviewMarking(submission)}
                      style={{
                        padding: '12px 24px',
                        background: '#c33329',
                        color: 'white',
                        border: 'none',
                        borderRadius: '8px',
                        cursor: 'pointer',
                        fontWeight: '600',
                        fontSize: '14px'
                      }}
                      title="Open the marking screen to see any marking already done, without approving or sending anything to the student"
                    >
                      Review Marking
                    </button>
                  )}
                  <button
                    onClick={() => onReturnToMarker(submission.id)}
                    style={{
                      padding: '12px 24px',
                      background: '#1b998b',
                      color: 'white',
                      border: 'none',
                      borderRadius: '8px',
                      cursor: 'pointer',
                      fontWeight: '600',
                      fontSize: '14px'
                    }}
                  >
                    Return to {submission.markerName || submission.flaggedByName || 'marker'}
                  </button>
                  <button
                    onClick={() => onReleaseToPool(submission.id)}
                    style={{
                      padding: '12px 24px',
                      background: 'white',
                      color: '#c33329',
                      border: '2px solid #c33329',
                      borderRadius: '8px',
                      cursor: 'pointer',
                      fontWeight: '600',
                      fontSize: '14px'
                    }}
                  >
                    Release to Available Pool
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export default FlaggedSubmissionsView;
